import { useEditor, EditorContent } from '@tiptap/react'
import { BubbleMenu } from '@tiptap/react/menus'
import { useEffect, useRef, useState } from 'react'
import { Button, Modal, message } from 'antd'
import { Input } from '@/components/ui/input'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import { TextAlign } from '@tiptap/extension-text-align'
import { TextStyle } from '@tiptap/extension-text-style'
import { FontFamily } from '@tiptap/extension-font-family'
import { Color } from '@tiptap/extension-color'
import Image from '@tiptap/extension-image'
import { Link } from '@tiptap/extension-link'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableCell } from '@tiptap/extension-table-cell'
import { TableHeader } from '@tiptap/extension-table-header'
import Placeholder from '@tiptap/extension-placeholder'

import {
  Undo2,
  Redo2,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Baseline,
  ListOrdered,
  List,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Image as ImageIcon,
  Link as LinkIcon,
  Table as TableIcon,
  Columns3,
  Rows3,
  Combine,
  UnfoldHorizontal,
  Trash2,
  ChevronDown,
} from 'lucide-react'

interface RichTextEditorProps {
  value?: string
  onChange?: (value: string) => void
  placeholder?: string
}

const Divider = () => (
  <div className="w-[1px] h-8 bg-gray-300 mx-1 flex-shrink-0" />
)

// Mở rộng Image để lưu thuộc tính class căn chỉnh
const CustomImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      class: {
        default: 'float-none',
        parseHTML: (element) => element.getAttribute('class') || 'float-none',
        renderHTML: (attributes) => ({
          class: attributes.class,
        }),
      },
    }
  },
})

export function RichTextEditor({
  value,
  onChange,
  placeholder = 'Nhập ...',
}: RichTextEditorProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const tableMenuRef = useRef<HTMLDivElement | null>(null)
  const tablePickerRef = useRef<HTMLDivElement | null>(null)

  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')
  const [linkLabel, setLinkLabel] = useState('')
  const [isSelectionEmpty, setIsSelectionEmpty] = useState(true)
  const [openTableMenu, setOpenTableMenu] = useState<'column' | 'row' | null>(
    null,
  )
  const [isTablePickerOpen, setIsTablePickerOpen] = useState(false)
  const [tableGridSize, setTableGridSize] = useState({ rows: 3, cols: 3 })

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      FontFamily,
      Color,
      Placeholder.configure({
        placeholder,
        emptyEditorClass: 'is-editor-empty',
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      CustomImage.configure({
        inline: true,
        allowBase64: true,
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
      }),
      Table.configure({
        resizable: true,
        lastColumnResizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: value,
    onUpdate: ({ editor: updatedEditor }) => {
      onChange?.(updatedEditor.getHTML())
    },
    editorProps: {
      attributes: {
        class:
          'min-h-[200px] p-4 focus:outline-none text-[15px] text-gray-700 leading-relaxed resize-y overflow-auto',
      },
    },
  })

  useEffect(() => {
    if (value === undefined) return
    if (value !== editor.getHTML() && !editor.isFocused) {
      editor.commands.setContent(value)
    }
  }, [editor, value])

  useEffect(() => {
    if (openTableMenu === null && !isTablePickerOpen) return

    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node
      const clickedOutsideMenus =
        !tableMenuRef.current?.contains(target) &&
        !tablePickerRef.current?.contains(target)

      if (clickedOutsideMenus) {
        setOpenTableMenu(null)
        setIsTablePickerOpen(false)
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [isTablePickerOpen, openTableMenu])

  const handleOpenLinkModal = () => {
    const previousUrl = editor.getAttributes('link').href || ''
    const empty = editor.state.selection.empty

    setLinkUrl(previousUrl)
    setLinkLabel('')
    setIsSelectionEmpty(empty)
    setIsLinkModalOpen(true)
  }

  const handleSubmitLink = (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (!linkUrl.trim()) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      setIsLinkModalOpen(false)
      return
    }

    if (!isSelectionEmpty) {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: linkUrl })
        .run()
    } else {
      const displayText = linkLabel.trim() !== '' ? linkLabel : linkUrl
      editor
        .chain()
        .focus()
        .insertContent(`<a href="${linkUrl}">${displayText}</a>`)
        .run()
    }

    setIsLinkModalOpen(false)
  }

  const handleImageToBase64 = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        const base64String = e.target?.result as string
        if (base64String) {
          editor
            .chain()
            .focus()
            .setImage({ src: base64String })
            .updateAttributes('image', { class: 'float-none' })
            .run()
          message.success('Chèn ảnh thành công!')
        }
      }
      reader.readAsDataURL(file)
    }
    event.target.value = ''
  }

  const handleInsertTable = (rows = 3, cols = 3) => {
    editor
      .chain()
      .focus()
      .insertTable({ rows, cols, withHeaderRow: true })
      .run()
  }

  const handleTableGridSelect = (rows: number, cols: number) => {
    setTableGridSize({ rows, cols })
    handleInsertTable(rows, cols)
    setIsTablePickerOpen(false)
  }

  const ToolbarButton = ({
    onClick,
    isActive = false,
    disabled = false,
    children,
    title,
    wide = false,
    className = '',
  }: any) => (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={`${wide ? 'h-8 px-2 gap-1' : 'w-8'} ${className} flex flex-shrink-0 items-center justify-center rounded-md transition-colors border-none outline-none ${
        isActive
          ? 'text-[#1E2F41] bg-gray-200'
          : 'bg-transparent text-[#1E2F41] hover:bg-gray-100'
      } ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      {children}
    </button>
  )

  return (
    <div className="relative w-full border border-solid border-gray-300 rounded-2xl bg-white overflow-hidden focus-within:border-[#CE7A58] focus-within:ring-[#CE7A58]/20 transition-all">
      <style>{`
        .ProseMirror img {
          max-width: 100%;
          height: auto;
          cursor: pointer;
          resize: both;
          overflow: auto;
        }

        .ProseMirror img.float-left {
          float: left !important;
          margin-right: 16px !important;
          margin-bottom: 12px !important;
          display: inline-block !important;
        }

        .ProseMirror img.float-right {
          float: right !important;
          margin-left: 16px !important;
          margin-bottom: 12px !important;
          display: inline-block !important;
        }

        .ProseMirror img.float-none {
          float: none !important;
          display: block !important;
          margin-left: auto !important;
          margin-right: auto !important;
          margin-top: 16px !important;
          margin-bottom: 16px !important;
        }

        .ProseMirror p {
          margin-bottom: 0.75em;
        }

        .ProseMirror::after {
          content: "";
          display: table;
          clear: both;
        }

        .ProseMirror p.is-editor-empty:first-child::before {
          color: #A0AAB4;
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }

        .ProseMirror a {
          color: #2563eb;
          text-decoration: underline;
          cursor: pointer;
        }

        .ProseMirror table {
          border-collapse: collapse;
          table-layout: fixed;
          width: 100%;
          margin: 1rem 0;
        }
        .ProseMirror td,
        .ProseMirror th {
          min-width: 1em;
          border: 1px solid #d1d5db;
          padding: 8px 10px;
          vertical-align: top;
          box-sizing: border-box;
          position: relative;
        }
        .ProseMirror th {
          font-weight: bold;
          text-align: left;
          background-color: #f9fafb;
        }
        .ProseMirror .selectedCell:after {
          z-index: 2;
          position: absolute;
          content: "";
          left: 0; right: 0; top: 0; bottom: 0;
          background: rgba(206, 122, 88, 0.15);
          pointer-events: none;
        }
      `}</style>

      <input
        type="file"
        ref={fileInputRef}
        className="!hidden"
        accept="image/*"
        onChange={handleImageToBase64}
      />

      {/* Bubble Menu Bảng */}
      <BubbleMenu
        editor={editor}
        shouldShow={({ editor: menuEditor }) => menuEditor.isActive('table')}
        className="flex items-center gap-1 p-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50"
      >
        <div ref={tableMenuRef} className="flex items-center gap-1">
          <div className="relative">
            <ToolbarButton
              className="w-9"
              title="Thao tác cột"
              onClick={() =>
                setOpenTableMenu((current) =>
                  current === 'column' ? null : 'column',
                )
              }
            >
              <span className="relative -left-px flex items-center">
                <Columns3 size={20} strokeWidth={1.8} />
                <ChevronDown size={14} strokeWidth={1.8} />
              </span>
            </ToolbarButton>
            {openTableMenu === 'column' && (
              <div className="absolute left-0 top-[calc(100%+6px)] z-[100] min-w-[210px] rounded-lg border border-gray-200 bg-white p-1.5 text-left shadow-xl">
                <button
                  type="button"
                  className="block w-full rounded-md px-3 py-2 text-left text-sm hover:bg-gray-100"
                  onClick={() => {
                    editor.chain().focus().toggleHeaderColumn().run()
                    setOpenTableMenu(null)
                  }}
                >
                  Bật/tắt cột tiêu đề
                </button>
                <button
                  type="button"
                  className="block w-full rounded-md px-3 py-2 text-left text-sm hover:bg-gray-100"
                  onClick={() => {
                    editor.chain().focus().addColumnBefore().run()
                    setOpenTableMenu(null)
                  }}
                >
                  Thêm cột bên trái
                </button>
                <button
                  type="button"
                  className="block w-full rounded-md px-3 py-2 text-left text-sm hover:bg-gray-100"
                  onClick={() => {
                    editor.chain().focus().addColumnAfter().run()
                    setOpenTableMenu(null)
                  }}
                >
                  Thêm cột bên phải
                </button>
                <button
                  type="button"
                  className="block w-full rounded-md px-3 py-2 text-left text-sm text-red-500 hover:bg-red-50"
                  onClick={() => {
                    editor.chain().focus().deleteColumn().run()
                    setOpenTableMenu(null)
                  }}
                >
                  Xóa cột
                </button>
              </div>
            )}
          </div>
          <Divider />
          <div className="relative">
            <ToolbarButton
              className="w-9"
              title="Thao tác dòng"
              onClick={() =>
                setOpenTableMenu((current) =>
                  current === 'row' ? null : 'row',
                )
              }
            >
              <span className="relative -left-px flex items-center">
                <Rows3 size={20} strokeWidth={1.8} />
                <ChevronDown size={14} strokeWidth={1.8} />
              </span>
            </ToolbarButton>
            {openTableMenu === 'row' && (
              <div className="absolute left-0 top-[calc(100%+6px)] z-[100] min-w-[210px] rounded-lg border border-gray-200 bg-white p-1.5 text-left shadow-xl">
                <button
                  type="button"
                  className="block w-full rounded-md px-3 py-2 text-left text-sm hover:bg-gray-100"
                  onClick={() => {
                    editor.chain().focus().toggleHeaderRow().run()
                    setOpenTableMenu(null)
                  }}
                >
                  Bật/tắt dòng tiêu đề
                </button>
                <button
                  type="button"
                  className="block w-full rounded-md px-3 py-2 text-left text-sm hover:bg-gray-100"
                  onClick={() => {
                    editor.chain().focus().addRowBefore().run()
                    setOpenTableMenu(null)
                  }}
                >
                  Thêm dòng phía trên
                </button>
                <button
                  type="button"
                  className="block w-full rounded-md px-3 py-2 text-left text-sm hover:bg-gray-100"
                  onClick={() => {
                    editor.chain().focus().addRowAfter().run()
                    setOpenTableMenu(null)
                  }}
                >
                  Thêm dòng phía dưới
                </button>
                <button
                  type="button"
                  className="block w-full rounded-md px-3 py-2 text-left text-sm text-red-500 hover:bg-red-50"
                  onClick={() => {
                    editor.chain().focus().deleteRow().run()
                    setOpenTableMenu(null)
                  }}
                >
                  Xóa dòng
                </button>
              </div>
            )}
          </div>
          <Divider />
          <ToolbarButton
            title="Gộp ô"
            onClick={() => editor.chain().focus().mergeCells().run()}
          >
            <Combine size={16} />
          </ToolbarButton>
          <ToolbarButton
            title="Tách ô"
            onClick={() => editor.chain().focus().splitCell().run()}
          >
            <UnfoldHorizontal size={16} />
          </ToolbarButton>
          <ToolbarButton
            title="Xóa bảng"
            onClick={() => editor.chain().focus().deleteTable().run()}
          >
            <Trash2 size={16} className="text-red-600" />
          </ToolbarButton>
        </div>
      </BubbleMenu>

      {/* Bubble Menu Ảnh: Nút hành động đơn giản, 100% không lỗi trạng thái */}
      <BubbleMenu
        editor={editor}
        shouldShow={({ editor: mEditor }) => mEditor.isActive('image')}
        className="flex items-center gap-1 p-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50"
      >
        <ToolbarButton
          title="Chữ tràn bên phải (Ảnh sát trái)"
          onClick={() =>
            editor
              .chain()
              .focus()
              .updateAttributes('image', { class: 'float-left' })
              .run()
          }
        >
          <AlignLeft size={16} />
        </ToolbarButton>

        <ToolbarButton
          title="Căn giữa ảnh"
          onClick={() =>
            editor
              .chain()
              .focus()
              .updateAttributes('image', { class: 'float-none' })
              .run()
          }
        >
          <AlignCenter size={16} />
        </ToolbarButton>

        <ToolbarButton
          title="Chữ tràn bên trái (Ảnh sát phải)"
          onClick={() =>
            editor
              .chain()
              .focus()
              .updateAttributes('image', { class: 'float-right' })
              .run()
          }
        >
          <AlignRight size={16} />
        </ToolbarButton>
      </BubbleMenu>

      <div className="flex items-center flex-wrap gap-0.5 p-3 border-b border-b-solid border-gray-300">
        <div className="flex items-center flex-shrink-0">
          <ToolbarButton
            title="Hoàn tác"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
          >
            <Undo2 size={18} strokeWidth={2} />
          </ToolbarButton>
          <ToolbarButton
            title="Làm lại"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
          >
            <Redo2 size={18} strokeWidth={2} />
          </ToolbarButton>
        </div>

        <Divider />

        <div className="flex items-center gap-1 flex-shrink-0">
          <select
            className="px-2 py-1.5 border border-solid border-gray-200 rounded-full text-sm bg-white hover:bg-gray-50 outline-none cursor-pointer text-[#1E2F41]"
            onChange={(e) => {
              if (e.target.value === 'default') {
                editor.chain().focus().unsetFontFamily().run()
              } else {
                editor.chain().focus().setFontFamily(e.target.value).run()
              }
            }}
          >
            <option value="default">Phông chữ</option>
            <option value="Arial">Arial</option>
            <option value="Times New Roman">Times New Roman</option>
            <option value="Courier New">Courier New</option>
            <option value="Georgia">Georgia</option>
          </select>
        </div>

        <Divider />

        <div className="flex items-center flex-shrink-0">
          <ToolbarButton
            title="In đậm"
            onClick={() => editor.chain().focus().toggleBold().run()}
            isActive={editor.isActive('bold')}
          >
            <Bold size={18} strokeWidth={2} />
          </ToolbarButton>
          <ToolbarButton
            title="In nghiêng"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isActive={editor.isActive('italic')}
          >
            <Italic size={18} strokeWidth={2} />
          </ToolbarButton>
          <ToolbarButton
            title="Gạch chân"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            isActive={editor.isActive('underline')}
          >
            <UnderlineIcon size={18} strokeWidth={2} />
          </ToolbarButton>
          <ToolbarButton
            title="Gạch ngang"
            onClick={() => editor.chain().focus().toggleStrike().run()}
            isActive={editor.isActive('strike')}
          >
            <Strikethrough size={18} strokeWidth={2} />
          </ToolbarButton>

          <div className="relative flex items-center justify-center w-8 h-8 rounded-md hover:bg-gray-100 cursor-pointer">
            <Baseline size={18} strokeWidth={2} className="text-[#1E2F41]" />
            <input
              type="color"
              title="Chọn màu chữ"
              className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
              onInput={(e) =>
                editor
                  .chain()
                  .focus()
                  .setColor((e.target as HTMLInputElement).value)
                  .run()
              }
            />
          </div>
        </div>

        <Divider />

        <div className="flex items-center flex-shrink-0">
          <ToolbarButton
            title="Danh sách chấm"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editor.isActive('bulletList')}
          >
            <List size={18} strokeWidth={2} />
          </ToolbarButton>
          <ToolbarButton
            title="Danh sách số"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editor.isActive('orderedList')}
          >
            <ListOrdered size={18} strokeWidth={2} />
          </ToolbarButton>
          <ToolbarButton
            title="Căn trái"
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            isActive={editor.isActive({ textAlign: 'left' })}
          >
            <AlignLeft size={18} strokeWidth={2} />
          </ToolbarButton>
          <ToolbarButton
            title="Căn giữa"
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            isActive={editor.isActive({ textAlign: 'center' })}
          >
            <AlignCenter size={18} strokeWidth={2} />
          </ToolbarButton>
          <ToolbarButton
            title="Căn phải"
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            isActive={editor.isActive({ textAlign: 'right' })}
          >
            <AlignRight size={18} strokeWidth={2} />
          </ToolbarButton>
          <ToolbarButton
            title="Căn đều"
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            isActive={editor.isActive({ textAlign: 'justify' })}
          >
            <AlignJustify size={18} strokeWidth={2} />
          </ToolbarButton>
        </div>

        <Divider />

        <div className="flex items-center flex-shrink-0">
          <ToolbarButton
            title="Chèn ảnh"
            onClick={() => fileInputRef.current?.click()}
          >
            <ImageIcon size={18} strokeWidth={2} />
          </ToolbarButton>
          <ToolbarButton
            title="Chèn liên kết"
            onClick={handleOpenLinkModal}
            isActive={editor.isActive('link')}
          >
            <LinkIcon size={18} strokeWidth={2} />
          </ToolbarButton>
          <div ref={tablePickerRef} className="relative">
            <ToolbarButton
              title="Chèn bảng"
              onClick={() => setIsTablePickerOpen((isOpen) => !isOpen)}
              isActive={editor.isActive('table') || isTablePickerOpen}
            >
              <TableIcon size={18} strokeWidth={2} />
            </ToolbarButton>
            {isTablePickerOpen && (
              <div
                className="absolute left-0 top-[calc(100%+6px)] z-[100] w-[270px] rounded-lg border border-gray-200 bg-white p-4 shadow-xl"
                onMouseDown={(event) => event.preventDefault()}
              >
                <div className="grid grid-cols-10 gap-1.5">
                  {Array.from({ length: 80 }, (_, index) => {
                    const row = Math.floor(index / 10) + 1
                    const col = (index % 10) + 1
                    const isHighlighted =
                      row <= tableGridSize.rows && col <= tableGridSize.cols

                    return (
                      <button
                        key={`${row}-${col}`}
                        type="button"
                        aria-label={`${col} cột, ${row} dòng`}
                        className={`appearance-none rounded-none border transition-colors ${
                          isHighlighted
                            ? 'border-[#CE7A58] bg-[#CE7A58]/25'
                            : 'border-gray-300 bg-white hover:border-[#CE7A58]'
                        }`}
                        style={{
                          width: 22,
                          height: 22,
                          minWidth: 22,
                          padding: 0,
                        }}
                        onMouseEnter={() =>
                          setTableGridSize({ rows: row, cols: col })
                        }
                        onClick={() => handleTableGridSelect(row, col)}
                      />
                    )
                  })}
                </div>
                <div className="mt-2 text-center text-xs font-medium text-gray-600">
                  {tableGridSize.cols} x {tableGridSize.rows}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="relative">
        <EditorContent editor={editor} />
      </div>

      <Modal
        open={isLinkModalOpen}
        onCancel={() => setIsLinkModalOpen(false)}
        title="Chèn liên kết"
        centered
        width={440}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button onClick={() => setIsLinkModalOpen(false)}>Hủy</Button>
            <Button type="primary" htmlType="submit" form="insert-link-form">
              Xác nhận
            </Button>
          </div>
        }
      >
        <form
          id="insert-link-form"
          onSubmit={handleSubmitLink}
          className="flex flex-col gap-3 mr-3"
        >
          <div>
            <label
              htmlFor="link-url-input"
              className="block text-xs font-medium text-gray-600 mb-1"
            >
              Đường dẫn (URL) <span className="text-red-500">*</span>
            </label>
            <Input
              id="link-url-input"
              type="url"
              required
              placeholder="https://example.com"
              value={linkUrl}
              onChange={(e: any) => setLinkUrl(e.target.value)}
              className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-[#CE7A58] focus:ring-1 focus:ring-[#CE7A58]"
            />
          </div>

          {isSelectionEmpty && (
            <div>
              <label
                htmlFor="link-label-input"
                className="block text-xs font-medium text-gray-600 mb-1"
              >
                Tên hiển thị
              </label>
              <Input
                id="link-label-input"
                type="text"
                placeholder="Ví dụ: Xem tại đây"
                value={linkLabel}
                onChange={(e: any) => setLinkLabel(e.target.value)}
                className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-[#CE7A58] focus:ring-1 focus:ring-[#CE7A58]"
              />
            </div>
          )}
        </form>
      </Modal>
    </div>
  )
}
